import { createOktaAuthForLogout } from "../oktaConfig";
import {
  OKTA_EXTERNAL_CONFIG,
  OKTA_INTERNAL_CONFIG,
} from "./appConstant";

export const DEACTIVATED_USER_SESSION_KEY = "showDeactivatedUserModal";
export const DEACTIVATED_URL_PARAM = "deactivated";
export const DEACTIVATED_OKTA_EXTERNAL_KEY = "deactivatedUserOktaExternal";
export const DEACTIVATED_PENDING_SIGNOUT_KEY = "deactivatedUserPendingSignOut";

const DEACTIVATION_KEYWORDS = [
  "deactivated",
  "disabled",
  "inactive",
  "not granted",
  "access denied",
  "access revoked",
  "no longer has access",
];

let deactivatedLogoutInProgress = false;

export const resetDeactivatedLogoutStateForTests = () => {
  deactivatedLogoutInProgress = false;
};

export const setDeactivatedUserModalFlag = () => {
  sessionStorage.setItem(DEACTIVATED_USER_SESSION_KEY, "true");
};

export const setDeactivatedPendingSignOut = () => {
  sessionStorage.setItem(DEACTIVATED_PENDING_SIGNOUT_KEY, "true");
};

export const hasDeactivatedPendingSignOut = () =>
  sessionStorage.getItem(DEACTIVATED_PENDING_SIGNOUT_KEY) === "true";

export const clearDeactivatedPendingSignOut = () => {
  sessionStorage.removeItem(DEACTIVATED_PENDING_SIGNOUT_KEY);
};

export const setDeactivatedOktaConfig = (isExternal) => {
  sessionStorage.setItem(DEACTIVATED_OKTA_EXTERNAL_KEY, String(isExternal));
};

export const clearDeactivatedOktaConfig = () => {
  sessionStorage.removeItem(DEACTIVATED_OKTA_EXTERNAL_KEY);
};

export const getStoredDeactivatedOktaExternal = () => {
  if (!hasDeactivatedUserModalSignal() && !hasDeactivatedPendingSignOut()) {
    return null;
  }

  const stored = sessionStorage.getItem(DEACTIVATED_OKTA_EXTERNAL_KEY);
  if (stored === null) {
    return null;
  }

  return stored === "true";
};

export const hasDeactivatedUserModalSignal = () => {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get(DEACTIVATED_URL_PARAM) === "1" ||
    sessionStorage.getItem(DEACTIVATED_USER_SESSION_KEY) === "true"
  );
};

export const shouldBlockLoginAutoFlow = () =>
  hasDeactivatedUserModalSignal() || hasDeactivatedPendingSignOut();

export const clearDeactivatedUserModalFlag = () => {
  sessionStorage.removeItem(DEACTIVATED_USER_SESSION_KEY);
};

export const clearStaleDeactivatedUserModalFlag = () => {
  if (!hasDeactivatedUserModalSignal()) {
    clearDeactivatedUserModalFlag();
  }
};

export const consumeDeactivatedUserModalFlag = () => {
  const shouldShow =
    sessionStorage.getItem(DEACTIVATED_USER_SESSION_KEY) === "true";
  if (shouldShow) {
    clearDeactivatedUserModalFlag();
  }
  return shouldShow;
};

export const clearDeactivatedSessionState = () => {
  clearDeactivatedUserModalFlag();
  clearDeactivatedPendingSignOut();
  clearDeactivatedOktaConfig();
};

export const getLoginRedirectUrl = () => `${window.location.origin}/login`;

export const isLoginRoute = (pathname = window.location.pathname) =>
  pathname === "/login" || pathname.startsWith("/login/");

export const cleanDeactivatedUrlParam = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get(DEACTIVATED_URL_PARAM) !== "1") {
    return;
  }

  params.delete(DEACTIVATED_URL_PARAM);
  const cleanUrl = params.toString()
    ? `${window.location.pathname}?${params}`
    : window.location.pathname;
  window.history.replaceState({}, "", cleanUrl);
};

const getResponsePayload = (response) => {
  const data = response?.data ?? response;
  return typeof data === "object" && data !== null ? data : {};
};

export const getApiResponseStatus = (response) => {
  if (!response) {
    return undefined;
  }
  return response?.status ?? response?.response?.status;
};

export const isDeactivatedUserResponse = (response) => {
  if (!response) {
    return false;
  }

  const payload = getResponsePayload(response);

  if (payload?.isGrantedAccess === false) {
    return true;
  }

  if (payload?.isActive === false || payload?.active === false) {
    return true;
  }

  if (String(payload?.status).toLowerCase() === "inactive") {
    return true;
  }

  const message = String(
    payload?.message ??
      payload?.error ??
      payload?.errorMessage ??
      response?.message ??
      ""
  ).toLowerCase();

  return DEACTIVATION_KEYWORDS.some((keyword) => message.includes(keyword));
};

export const clearAuthStorage = () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("okta-token-storage");
  localStorage.removeItem("auth_state");
};

export const shouldShowDeactivatedUserModal = () => {
  const shouldShow = hasDeactivatedUserModalSignal();
  if (shouldShow) {
    cleanDeactivatedUrlParam();
  }
  return shouldShow;
};

export const inferIsExternalFromTokenStorage = () => {
  try {
    const raw = localStorage.getItem("okta-token-storage");
    if (!raw) {
      return null;
    }

    const data = JSON.parse(raw);
    const issuer =
      data?.idToken?.claims?.iss ??
      data?.accessToken?.claims?.iss ??
      data?.idToken?.issuer ??
      "";

    if (!issuer) {
      return null;
    }

    const externalIssuerBase = OKTA_EXTERNAL_CONFIG.issuer.split("/oauth2")[0];
    const internalIssuerBase = OKTA_INTERNAL_CONFIG.issuer.split("/oauth2")[0];

    if (issuer.startsWith(externalIssuerBase)) {
      return true;
    }

    if (issuer.startsWith(internalIssuerBase)) {
      return false;
    }
  } catch (error) {
    console.error("Unable to infer Okta issuer from token storage:", error);
  }

  return null;
};

export const resolveDeactivatedLogoutIsExternal = (reduxExternal) => {
  if (reduxExternal === true) {
    return true;
  }

  if (reduxExternal === false) {
    return false;
  }

  const storedExternal = getStoredDeactivatedOktaExternal();
  if (storedExternal !== null) {
    return storedExternal;
  }

  return inferIsExternalFromTokenStorage() ?? false;
};

export const performOktaLogout = async (reduxExternal = null) => {
  const isExternal = resolveDeactivatedLogoutIsExternal(reduxExternal);
  const loginUrl = getLoginRedirectUrl();
  const oktaAuth = createOktaAuthForLogout(isExternal);

  try {
    await oktaAuth.signOut({
      postLogoutRedirectUri: loginUrl,
    });
  } catch (error) {
    console.error("Error during Okta sign out:", error);
    clearAuthStorage();
    window.location.replace(loginUrl);
  }
};

export const handleDeactivatedUserLogout = async (reduxExternal = null) => {
  if (deactivatedLogoutInProgress) {
    return;
  }

  deactivatedLogoutInProgress = true;
  setDeactivatedUserModalFlag();
  setDeactivatedPendingSignOut();
  setDeactivatedOktaConfig(resolveDeactivatedLogoutIsExternal(reduxExternal));
  await performOktaLogout(reduxExternal);
};

export const completeOktaSignOutIfAuthenticated = async (authState) => {
  if (!authState?.isAuthenticated) {
    clearDeactivatedPendingSignOut();
    clearDeactivatedOktaConfig();
    return;
  }

  await performOktaLogout(null);
};

export const getInitialDeactivatedModalState = () =>
  hasDeactivatedUserModalSignal();
